import { Map, Record } from "immutable";
import update, { Query } from "immutability-helper";
import _ from "lodash";

import { ApiModel } from "@api/models";
import { ApiRequest } from "@api/requests";
import { snakeCaseToCamelCase } from "./functions";

abstract class Model {
    public primaryKey: string = "id";

    /**
     * Function to fill this model in from the given ApiModel.
     *
     * @param ApiModel apiModel - The model to fill from
     * @param string[] ignore - The properties to ignore
     * @return void
     */
    public fillFromApiModel(apiModel: ApiModel, ignore: string[] = []): void {
        // Create an intermediary object to convert the properties.
        const obj: any = new Object();

        // Now assign the properties from the apiModel to the intermediary,
        // converting the names, where relevant.
        const apiModelMap = Map<string, any>(apiModel);
        apiModelMap.map((value, prop) => {
            const camelCaseProp: string = snakeCaseToCamelCase(prop);

            // Ignore anything in the ignore array.
            if (ignore.indexOf(camelCaseProp) === -1 && this.hasOwnProperty(camelCaseProp)) {
                // Loop through arrays.
                if (Array.isArray(value)) {
                    obj[camelCaseProp] = [];
                    value.map((_val, key) => {
                        obj[camelCaseProp][key] = value[key];
                    });
                }
                else {
                    obj[camelCaseProp] = value;
                }
            }
        });

        // Finally, assign the object to this.
        Object.assign(this, obj);
    }
}

interface RestModel {
    exists?: boolean;
    lastLoaded?: Date;
    hydrated?: boolean;
    viewUrl?: string;
    viewText?: string;
}

const RestRecord = (defaultValues: {[key: string]: any}) => {
    return class extends Record({
        exists: false,
        lastLoaded: null,
        hydrated: false,
        viewUrl: null,
        viewText: null,
        ...defaultValues,
    }) implements RestModel {
        public static ID: string = "id";
        public static RELATIONS: string[] = [];

        public exists: boolean;
        public lastLoaded: Date;
        public hydrated: boolean;
        public viewUrl: string;
        public viewText: string;

        public setCast(key: string, value: any) {
            return this.set(key, value) as this;
        }
    };
};

type MapLoadedModel = (loadedModels: string[], entities: Map<string | number, RestModel>)
    => (entity: RestModel) => RestModel;

/**
 * Function to make a function that makes a function to map a loaded model,
 * considering existing relations, `lastLoaded` and `exists`.
 */
// HACK Trying to access static parameters from `type` proved problematic, so
// just using `any` here
const makeMapLoadedModel = <E extends Map<string, any>, K = number>(model: string, type: any | RestModel) =>
    (loadedModels: string[], entities: Map<K, E>) => (entity: E): E => {
        // Retain lastLoaded and relations from the existing entity.
        const existing = entities.get(entity.get(type.ID));
        if (existing != null) {
            entity = entity.set("lastLoaded", existing.get("lastLoaded")) as E;

            // Go through all properties and only overwrite the undefined here?
            existing.forEach((val: any, key: string) => {
                if (!entity.has(key) || entity.get(key) === undefined) {
                    entity = entity.set(key, val) as E;
                }
            });

            for (const rel of type.RELATIONS) {
                if (typeof entity.get(rel) === "undefined") {
                    entity = entity.set(rel, existing.get(rel)) as E;
                }
            }
        }
        // Set the entity as loaded and existing.
        if (loadedModels.indexOf(model) > -1) {
            entity = entity.set("lastLoaded", new Date()) as E;
        }
        entity = entity.set("exists", true) as E;
        return entity;
    };

interface NormalisedEntities {
    [key: string]: Map<number | string, any>;
}

interface State<NE extends NormalisedEntities> {
    entities: NE;
}

interface MapLoadedModelsMap {
    [key: string]: MapLoadedModel;
}

/**
 * Make a function to merge a normalised entities object into state.
 */
const makeMergeEntities = <S extends State<NE>, NE extends NormalisedEntities>
    (allowedMaps: string[], mapLoadedModels: MapLoadedModelsMap = {}) =>
        (state: S, entities: NE, ...loadedModels: string[]): S => {
            // Set up the new state
            const newState = {
                entities: update(state.entities, {}),
            } as S;

            // Merge each map.
            newState.entities = state.entities;
            _.forOwn(entities, (entityMap, key) => {
                if (entityMap != null && allowedMaps.indexOf(key) > -1) {
                    // Handle mapping loaded models.
                    const newEntities: Query<keyof NE> = {};
                    if (key in mapLoadedModels) {
                        const map = mapLoadedModels[key](loadedModels, state.entities[key]);
                        const mappedEntities = entityMap.map(map);
                        newEntities[key] = { $merge: state.entities[key].merge(mappedEntities) };
                    }
                    else {
                        newEntities[key] = { $merge: state.entities[key].merge(entityMap) };
                    }
                    newState.entities = update(newState.entities, newEntities);
                }
            });

            return update(state, {
                entities: { $merge: newState.entities },
            });
        };

/**
 * Make a function to handle a failed show action.
 */
const makeHandleShowFailure = <S extends State<NE>, C, NE extends NormalisedEntities>
    (type: new(obj: any) => C, entitiesKey: keyof NE, hasNumberKey: boolean = true) =>
        (state: S, { request }: HttpStateError): S => {
            const newState = {
                entities: state.entities,
            } as S;
            // Add a non-existent delivery to the store
            let id: number | string = null;
            if (request != null) {
                if (hasNumberKey) {
                    id = request.getId();
                }
                else {
                    id = request.getIdStr();
                }
            }
            if (id != null) {
                const model = new type({
                    id,
                    lastLoaded: new Date(),
                    exists: false,
                });
                newState.entities[entitiesKey] = newState.entities[entitiesKey].set(id, model) as NE[keyof NE];
            }
            return update(state, {
                entities: { $merge: newState.entities },
            });
        };

interface HasCurrency {
    currencyCode: string;
}

interface IdPair {
    id: number;
    secondId: number;
}

interface ModelCollectionState<K, V> {
    items: Map<K, V>;
    itemsLastLoaded: Date;
    error: StateError;
}

interface PaginationResult {
    count: number;
    page: number;
    perPage: number;
    total?: number;
    numPages?: number;
    nextLink: string;
    previousLink: string;
    pagingLinkTemplate: string;
}

interface TrendsPaginationResult extends PaginationResult {
    from: Date;
    to: Date;
    albumId?: number;
    trackId?: number;
    artistId?: number;
}

function getParamFromPagination(pagination: PaginationResult, name: string): string {
    const url = new URL(pagination.pagingLinkTemplate);
    const iter = url.searchParams.entries();
    let entry = iter.next();
    while (!entry.done) {
        const key = decodeURIComponent(entry.value[0]);
        if (key === name) {
            return entry.value[1];
        }
        entry = iter.next();
    }
    return null;
}

function getArrayParamFromPagination(pagination: PaginationResult, name: string): string[] {
    const url = new URL(pagination.pagingLinkTemplate);
    const iter = url.searchParams.entries();
    let entry = iter.next();
    const values: string[] = [];
    while (!entry.done) {
        const key = decodeURIComponent(entry.value[0]);
        if (key.match(new RegExp(`^${name}\\[[0-9]+\\]$`))) {
            values.push(entry.value[1]);
        }
        entry = iter.next();
    }
    return values.length > 0 ? values : null;
}

interface NormalisedResult<K, T> {
    entities: T;
    result: K;
}

type StateErrorType = "HTTP" | "Validation";

interface StateError {
    type: StateErrorType;
    message: string;
    errors?: StateError[];
    key?: string;
}

interface HttpStateError extends StateError {
    type: "HTTP";
    request: ApiRequest;
    code?: number;
}

function isHttpStateError(err: StateError): err is HttpStateError {
    return err.type === "HTTP";
}

function isHttpStateErrorUnauthenticated(err: StateError): err is HttpStateError {
    return isHttpStateError(err) && err.code === 401;
}

interface InnerStateWithPagination {
    pagination: PaginationResult;
}

export {
    Model,
    RestModel,
    RestRecord,
    makeMapLoadedModel,
    NormalisedEntities,
    makeMergeEntities,
    makeHandleShowFailure,
    HasCurrency,
    IdPair,
    ModelCollectionState,
    PaginationResult,
    TrendsPaginationResult,
    getParamFromPagination,
    getArrayParamFromPagination,
    NormalisedResult,
    StateError,
    HttpStateError,
    isHttpStateError,
    isHttpStateErrorUnauthenticated,
    InnerStateWithPagination,
};
